/**
 * Composant Organigramme - Graphique organisationnel compact
 * Conçu pour tenir dans la modale (max-w-5xl) sans scroll horizontal
 */

import {
  type DocumentComponent,
  type DocumentProps,
  type VariablesData,
} from "../../lib/types";
import { cn } from "@repo/utils";
import { createElement } from "react";

function OrgCard({
  title,
  name,
  subtitle,
  isYou,
  className,
}: {
  title: string;
  name?: string;
  subtitle?: string;
  isYou?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-surface border bg-card px-3 py-2 text-center shadow-sm",
        isYou && "border-primary ring-2 ring-primary/20",
        className,
      )}
    >
      <p className="text-xs font-semibold leading-tight text-foreground">
        {title}
      </p>
      {name && (
        <p
          className={cn(
            "mt-0.5 text-[11px] text-muted-foreground",
            isYou && "font-bold text-primary",
          )}
        >
          {name}
        </p>
      )}
      {subtitle && (
        <p className="text-[10px] text-muted-foreground/70">{subtitle}</p>
      )}
    </div>
  );
}

function VLine({ className }: { className?: string }) {
  return <div className={cn("mx-auto h-5 w-px bg-border", className)} />;
}

/** Colonne de service : responsable + sous-services facultatifs listés à la verticale + effectif */
function DeptColumn({
  title,
  name,
  subServices,
  staff,
}: {
  title: string;
  name: string;
  subServices?: { role: string; name: string }[];
  staff: string[];
}) {
  return (
    <div className="flex flex-1 flex-col items-center">
      {/* Department head */}
      <div className="w-full rounded-surface border bg-card px-2 py-2 text-center shadow-sm">
        <p className="text-[11px] font-semibold text-foreground">{title}</p>
        <p className="text-[11px] text-muted-foreground">{name}</p>
      </div>

      {/* Sub-services listed vertically */}
      {subServices && subServices.length > 0 && (
        <>
          <VLine className="h-3" />
          <div className="w-full space-y-1">
            {subServices.map((s, i) => (
              <div
                key={i}
                className="rounded-surface border border-dashed bg-card px-2 py-1 text-center"
              >
                <p className="text-[10px] font-medium text-foreground">
                  {s.role}
                </p>
                <p className="text-[10px] text-muted-foreground">{s.name}</p>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Staff counts */}
      <VLine className="h-3" />
      <div className="w-full rounded-surface bg-muted/60 px-2 py-1.5 text-center">
        {staff.map((line, i) => (
          <p key={i} className="text-[10px] text-muted-foreground">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

function OrganisationChart({
  variablesData: v,
}: {
  variablesData: VariablesData;
}) {
  return (
    <div className="w-full space-y-0">
      {/* Level 1: Board — 3 members in a row */}
      <div className="flex items-center justify-center gap-2">
        <OrgCard
          className="max-w-48"
          title="Vice-Président Admin. Générale"
          name={`${v.vice_president_administration_generale_prenom} ${v.vice_president_administration_generale_nom}`}
        />
        <div className="h-px w-10 bg-border" />
        <OrgCard
          className="max-w-40"
          title="Présidente du C.A."
          name={`${v.presidente_CA_prenom} ${v.presidente_CA_nom}`}
        />
        <div className="h-px w-10 bg-border" />
        <OrgCard
          className="max-w-48"
          title="Vice-Président Développement"
          name={`${v.vice_president_charge_developpement_prenom} ${v.vice_president_charge_developpement_nom}`}
        />
      </div>

      <VLine />

      {/* Level 2: DG */}
      <div className="flex justify-center">
        <OrgCard
          title={v.directeur_general_fonction || "Directeur Général"}
          name={`${v.directeur_general_prenom} ${v.directeur_general_nom}`}
        />
      </div>

      <VLine />

      {/* Level 3: You + Secretary */}
      <div className="flex items-center justify-center gap-2">
        <OrgCard
          title={
            v.responsable_grand_est_precedent_fonction_organigramme ||
            "Directeur Régional Grand Est"
          }
          name={`VOUS (${v.responsable_grand_est_actuel_prenom} ${v.responsable_grand_est_actuel_nom})`}
          subtitle={`remplace ${v.genre_masc_abrege} ${v.responsable_grand_est_precedent_nom}`}
          isYou
        />
        <div className="h-px w-10 bg-border" />
        <OrgCard
          title="Secrétariat"
          name={`${v.secretaire_prenom} ${v.secretaire_nom}`}
        />
      </div>

      {/* Branch connector to 5 departments */}
      <div className="flex flex-col items-center">
        <VLine />
        <div className="mx-8 h-px w-full bg-border" />
        <div className="mx-8 flex w-full justify-between">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className="h-4 w-px bg-border" />
            </div>
          ))}
        </div>
      </div>

      {/* Level 4-6: 5 Department columns */}
      <div className="flex items-start gap-2">
        <DeptColumn
          title="Commercial"
          name={`${v.responsable_commercial_prenom} ${v.responsable_commercial_nom}`}
          staff={[
            "5 technico-commerciaux",
            `dont ${v.cadre_technico_commercial_nom}`,
          ]}
        />

        <DeptColumn
          title="Administration"
          name={`${v.responsable_administration_prenom} ${v.responsable_administration_nom}`}
          subServices={[
            {
              role: "Personnel / Accueil",
              name: `${v.administration_personnel_accueil_prenom} ${v.administration_personnel_accueil_nom}`,
            },
            {
              role: "Comptabilité",
              name: `${v.administration_comptabilite_prenom} ${v.administration_comptabilite_nom}`,
            },
            {
              role: "Informatique",
              name: `${v.administration_informatique_prenom} ${v.administration_informatique_nom}`,
            },
          ]}
          staff={[
            "2 Agents de Maîtrise",
            `(dont ${v.delegue_syndical_nom}, resp. syndical)`,
            "6 employés, 2 analystes-prog.",
          ]}
        />

        <DeptColumn
          title="Études et Procédés"
          name={`${v.etudes_et_procedes_prenom} ${v.etudes_et_procedes_nom}`}
          staff={["6 ingénieurs", "10 maîtrises"]}
        />

        <DeptColumn
          title="Production"
          name={`${v.responsable_production_prenom} ${v.responsable_production_nom}`}
          subServices={[
            {
              role: "Achats",
              name: `${v.production_achats_prenom} ${v.production_achats_nom}`,
            },
            {
              role: "Logistique",
              name: `${v.production_logistique_prenom} ${v.production_logistique_nom}`,
            },
            {
              role: "Fabrication",
              name: `${v.production_fabrication_prenom} ${v.production_fabrication_nom}`,
            },
          ]}
          staff={[
            "3 ingénieurs, 9 techniciens",
            "2 employés, 36 ouvriers",
          ]}
        />

        <DeptColumn
          title="Entretien et Sécurité"
          name={`${v.entretien_et_securite_prenom} ${v.entretien_et_securite_nom}`}
          staff={["1 ingénieur", "3 techniciens", "2 ouvriers"]}
        />
      </div>
    </div>
  );
}

/**
 * Composant Organigramme avec métadonnées
 */
export const Organigramme: DocumentComponent = function Organigramme({
  variablesData,
}: DocumentProps) {
  return createElement(OrganisationChart, { variablesData });
};

Organigramme.metadata = {
  id: "organigramme",
  titre: "Organigramme SYTEL Grand Est",
  icon: "org",
};

export default Organigramme;
